import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Workflow } from '../../types';

interface SearchableWorkflowDropdownProps {
  workflows: Workflow[];
  selectedWorkflow: Workflow | null;
  onSelect: (workflow: Workflow) => void;
  onToggleFavorite?: (workflowId: number, event: React.MouseEvent) => void;
  placeholder?: string;
  getCategoryIcon: (category: string) => string;
}

export interface SearchableWorkflowDropdownRef {
  openAndSearch: (char: string) => void;
}

const SearchableWorkflowDropdown = forwardRef<SearchableWorkflowDropdownRef, SearchableWorkflowDropdownProps>(({
  workflows,
  selectedWorkflow,
  onSelect,
  onToggleFavorite,
  placeholder = "Choose a workflow...",
  getCategoryIcon
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter workflows based on search term
  const filteredWorkflows = workflows.filter(workflow =>
    workflow.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm(''); // Clear search when closing
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Expose method to parent component
  useImperativeHandle(ref, () => ({
    openAndSearch: (char: string) => {
      setIsOpen(true);
      setSearchTerm(char);
      // Focus will happen via useEffect when isOpen changes
    }
  }));

  const handleSelect = (workflow: Workflow) => {
    onSelect(workflow);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm(''); // Clear search when opening
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={handleToggle}
        className="w-full px-3 py-2.5 text-left border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors cursor-pointer hover:border-gray-400"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {selectedWorkflow ? (
              <>
                <span className="text-base flex-shrink-0">{getCategoryIcon(selectedWorkflow.category)}</span>
                <span className="text-gray-900 font-medium truncate">{selectedWorkflow.name}</span>
              </>
            ) : (
              <span className="text-gray-500">{placeholder}</span>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {selectedWorkflow && onToggleFavorite && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFavorite(selectedWorkflow.id, e);
                }}
                className="text-lg hover:scale-110 transition-transform p-1"
              >
                {selectedWorkflow.is_favorite ? '⭐' : '☆'}
              </button>
            )}
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
          {/* Search Input Inside Dropdown */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search workflows..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 pl-9 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onClick={(e) => e.stopPropagation()}
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Workflow Options */}
          <div className="max-h-80 overflow-y-auto">
            {filteredWorkflows.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-gray-500">
                No workflows found
              </div>
            ) : (
              filteredWorkflows.map((workflow) => (
                <button
                  key={workflow.id}
                  type="button"
                  onClick={() => handleSelect(workflow)}
                  className={`w-full px-3 py-2.5 text-left text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors cursor-pointer flex items-center justify-between ${
                    selectedWorkflow?.id === workflow.id
                      ? 'bg-blue-100 text-blue-700 font-medium'
                      : 'text-gray-900'
                  }`}
                >
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <span className="text-base flex-shrink-0">{getCategoryIcon(workflow.category)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{workflow.name}</div>
                      <div className="text-xs text-gray-500 truncate">
                        {workflow.category} • {workflow.workflow_type === 'slug'
                          ? `${workflow.override_count || 0} params`
                          : `${Object.keys(workflow.configurable_params || {}).length} params`}
                      </div>
                    </div>
                  </div>
                  {selectedWorkflow?.id === workflow.id && (
                    <svg className="w-4 h-4 text-blue-600 flex-shrink-0 ml-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
});

SearchableWorkflowDropdown.displayName = 'SearchableWorkflowDropdown';

export default SearchableWorkflowDropdown;
